import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Grid, Paper, Typography, Button, Box, Chip } from '@mui/material';
import { useSystemNotificationDetailQuery } from '../../../types/generated-types';
import { useAuthorizer } from '../../auth/AuthorizationContext';
import { useInjectableComponents } from '../../system/services/injectableComponentsManager';
import { Edit, Delete } from '@mui/icons-material';
import { Actions } from '../../auth/types/ability';

export const SystemNotificationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useAuthorizer();
  const { setContextMenuItems, setTitle } = useInjectableComponents();

  const { data, loading, error } = useSystemNotificationDetailQuery({
    variables: { _id: id! },
    skip: !id,
  });

  // Set the page title
  useEffect(() => {
    if (data?.systemNotificationById) {
      setTitle(
        `System Notification: ${data.systemNotificationById.message.substring(0, 50)}${data.systemNotificationById.message.length > 50 ? '...' : ''}`,
      );
    } else {
      setTitle('System Notification');
    }
    return () => {
      setTitle(undefined);
    };
  }, [data?.systemNotificationById, setTitle]);

  // Set the context menu items
  useEffect(() => {
    if (data?.systemNotificationById) {
      const menuItems = [
        {
          id: 'edit-system-notification-menu-item',
          label: 'Edit System Notification',
          icon: <Edit fontSize="small" />,
          action: () => navigate(`/system-notifications/${id}/edit`),
          permit: {
            action: 'update' as Actions,
            subject: { __typename: 'SystemNotification', _id: id! },
          },
        },
        {
          id: 'delete-system-notification-menu-item',
          label: 'Delete System Notification',
          icon: <Delete fontSize="small" />,
          action: () => {
            // TODO: Implement delete functionality
            console.log('Delete system notification:', id);
          },
          permit: {
            action: 'delete' as Actions,
            subject: { __typename: 'SystemNotification', _id: id! },
          },
        },
      ];

      setContextMenuItems([
        {
          items: menuItems,
        },
      ]);
    } else {
      setContextMenuItems(undefined);
    }
    return () => {
      setContextMenuItems(undefined);
    };
  }, [data?.systemNotificationById, id, navigate, setContextMenuItems]);

  if (loading) {
    return (
      <div>
        <Typography variant="h6">Loading system notification...</Typography>
      </div>
    );
  }

  if (error || !data?.systemNotificationById) {
    return (
      <div>
        <Typography variant="h6" color="error">
          Failed to load system notification
        </Typography>
      </div>
    );
  }

  const notification = data.systemNotificationById;

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  const getStatusChip = () => {
    const now = Date.now();
    if (now < notification.displayStart) {
      return <Chip label="Scheduled" color="warning" />;
    } else if (now > notification.displayEnd) {
      return <Chip label="Expired" color="error" />;
    } else if (notification.acknowledgedAt) {
      return <Chip label="Acknowledged" color="success" />;
    } else {
      return <Chip label="Active" color="primary" />;
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4">System Notification</Typography>
        <Box>
          {can('update', { __typename: 'SystemNotification' }) && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(`/system-notifications/${id}/edit`)}
              style={{ marginRight: '10px' }}
            >
              Edit
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={() => navigate('/system-notifications')}
          >
            Back to List
          </Button>
        </Box>
      </Box>

      <div>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Notification Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Message
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {notification.message}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Related Entity
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {notification.relatedEntityName} ({notification.entityType})
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Link
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {notification.link ? (
                      <a
                        href={notification.link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {notification.link}
                      </a>
                    ) : (
                      'No link provided'
                    )}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Status
                  </Typography>
                  {getStatusChip()}
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Acknowledgement Settings
                  </Typography>
                  <Typography variant="body1" paragraph>
                    Require Acknowledgement:{' '}
                    {notification.requireAcknowledgement ? 'Yes' : 'No'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Timing Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Created
                  </Typography>
                  <Typography variant="body2">
                    {formatTimestamp(notification.createdAt)}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Display Start
                  </Typography>
                  <Typography variant="body2">
                    {formatTimestamp(notification.displayStart)}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Display End
                  </Typography>
                  <Typography variant="body2">
                    {formatTimestamp(notification.displayEnd)}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Acknowledged
                  </Typography>
                  <Typography variant="body2">
                    {notification.acknowledgedAt
                      ? formatTimestamp(notification.acknowledgedAt)
                      : 'Not acknowledged'}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Seen At
                  </Typography>
                  <Typography variant="body2">
                    {notification.seenAt
                      ? formatTimestamp(notification.seenAt)
                      : 'Not seen'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};
